const TTS_ENABLED_KEY = 'ttsEnabled';
const TTS_VOICE_KEY = 'ttsVoice';
const LANGUAGE_CODE = 'en-GB';
export class TTS {
    static async enableInSettings() {
        try {
            localStorage.setItem(TTS_ENABLED_KEY, JSON.stringify(true));
        }
        catch (error) {
            console.error('Error enabling TTS in settings:', error);
        }
    }
    static async disableInSettings() {
        try {
            localStorage.setItem(TTS_ENABLED_KEY, JSON.stringify(false));
        }
        catch (error) {
            console.error('Error disabling TTS in settings:', error);
        }
    }
    static async isEnabled() {
        try {
            const value = localStorage.getItem(TTS_ENABLED_KEY);
            return value ? JSON.parse(value) : true; // Default to enabled
        }
        catch (error) {
            console.error('Error checking TTS enabled state:', error);
            return true; // Default to enabled on error
        }
    }
    static async getVoices() {
        try {
            const voices = await TTS.loadVoices();
            return voices.filter(v => v.lang.startsWith('en')).map(v => v.name);
        }
        catch (error) {
            console.error('Error getting TTS voices:', error);
            return [];
        }
    }
    static async setVoice(voice) {
        try {
            localStorage.setItem(TTS_VOICE_KEY, voice);
        }
        catch (error) {
            console.error('Error setting TTS voice:', error);
        }
    }
    static async getSelectedVoice() {
        try {
            return localStorage.getItem(TTS_VOICE_KEY);
        }
        catch (error) {
            console.error('Error getting selected TTS voice:', error);
            return null;
        }
    }
    static isSpeaking() {
        return TTS.speaking;
    }
    static loadVoices() {
        return new Promise((resolve) => {
            const voices = window.speechSynthesis.getVoices();
            if (voices.length > 0) {
                resolve(voices);
            }
            else {
                window.speechSynthesis.onvoiceschanged = () => {
                    resolve(window.speechSynthesis.getVoices());
                };
            }
        });
    }
    static async doSpeak(text, voice, attemptSpeech = 0) {
        return new Promise((resolve) => {
            TTS.speaking = true;
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = voice?.lang || LANGUAGE_CODE;
            utterance.rate = 1.0;
            utterance.pitch = 1.0;
            utterance.volume = 1.0;
            if (voice) {
                utterance.voice = voice;
            }
            utterance.onend = () => {
                TTS.speaking = false;
                resolve();
            };
            utterance.onerror = (e) => {
                if (e.error === 'not-allowed' && attemptSpeech < 4) {
                    setTimeout(() => TTS.doSpeak(text, voice, attemptSpeech + 1), 250);
                }
                else {
                    TTS.speaking = false;
                    resolve();
                }
            };
            window.speechSynthesis.speak(utterance);
        });
    }
    static async speak(text) {
        try {
            const isEnabled = await this.isEnabled();
            if (!isEnabled) {
                return;
            }
            const selectedVoiceName = await this.getSelectedVoice();
            const voices = await TTS.loadVoices();
            // Find the selected voice object
            let voiceToUse = voices.find(v => v.name === selectedVoiceName && v.lang.startsWith('en')) ?? null;
            // If no voice is selected or voice not found, use default English voice
            if (!voiceToUse) {
                voiceToUse = voices.find(v => v.lang.toLowerCase().startsWith('en-gb'))
                    || voices[0]
                    || null;
            }
            await TTS.doSpeak(text, voiceToUse);
        }
        catch (error) {
            console.error('Error speaking text:', error);
        }
    }
    static async stop() {
        try {
            TTS.speaking = false;
            window.speechSynthesis.cancel();
        }
        catch (error) {
            console.error('Error stopping TTS:', error);
        }
    }
    static async getAvailableLanguages() {
        try {
            const voices = await TTS.loadVoices();
            return [...voices.map(v => v.lang)];
        }
        catch (error) {
            console.error('Error getting available languages:', error);
            return [];
        }
    }
    static async getVoicesForLanguage(language) {
        try {
            const voices = await TTS.loadVoices();
            return voices.filter(v => v.lang === language).map(v => v.name);
        }
        catch (error) {
            console.error('Error getting voices for language:', error);
            return [];
        }
    }
}
TTS.speaking = false;
